/**
 * FeedbackButton — always-visible FAB + inline feedback card.
 * First 3 views: FAB intro pill "Fortæl os hvad du tænker" → collapses to icon.
 * Textarea always visible. Grace period: 3 sessions for auto-triggers.
 */
import { useState, useEffect, useCallback, useImperativeHandle, forwardRef, useRef } from 'react'
import { View, Text, Pressable, TextInput, StyleSheet, Platform } from 'react-native'
import Animated, {
  useSharedValue, useAnimatedStyle, withTiming,
  SlideInDown, FadeOutDown, FadeIn,
} from 'react-native-reanimated'
import { MessageSquareHeart, X, Send, MessageCircle } from 'lucide-react-native'
import { usePathname } from 'expo-router'
import { useFeedback, type FeedbackTrigger } from '../hooks/useFeedback'
import { appEvents } from '../lib/events'
import { events } from '../lib/posthog'
import { haptics } from '../utils/haptics'
import { useFeatureFlags } from '../context/FeatureFlagsContext'
import { useResponsive } from '../hooks/useResponsive'
import { colors, spacing, borderRadius, typography, shadows } from '../theme'
import { toast } from './Toast'

export interface FeedbackButtonHandle {
  openWithTrigger: (trigger: FeedbackTrigger, placeholder?: string) => void
}

// Global trigger API — call from any screen
let globalOpenFeedback: ((trigger: FeedbackTrigger, placeholder?: string) => void) | null = null
export const feedbackTrigger = {
  open: (trigger: FeedbackTrigger, placeholder?: string) => globalOpenFeedback?.(trigger, placeholder),
}

const EMOJI_OPTIONS = [
  { emoji: '😍', rating: 5 },
  { emoji: '😊', rating: 4 },
  { emoji: '😐', rating: 3 },
  { emoji: '😕', rating: 2 },
  { emoji: '😤', rating: 1 },
] as const

const TRIGGER_QUESTIONS: Record<FeedbackTrigger, string> = {
  manual: 'Er der noget i appen du kunne tænke dig var anderledes?',
  post_onboarding: 'Var der noget undervejs der var forvirrende eller tog for lang tid?',
  first_quote_view: 'Giver tilbuddene mening, eller mangler du noget i oversigten?',
  quote_accepted: 'Føltes det trygt at acceptere? Var der noget du var i tvivl om?',
  chat_engaged: 'Fik du svar på det du ledte efter, eller mangler der noget?',
  inbox_explored: 'Er din indbakke nem at finde rundt i, eller er noget forvirrende?',
  settings_visited: 'Er der noget i indstillingerne du savner eller gerne ville ændre?',
  dashboard_return: 'Er der noget på forsiden du synes mangler eller kunne være bedre?',
  error_encountered: 'Noget gik galt — kan du fortælle hvad du prøvede at gøre?',
  returning_user: 'Hvad ville få dig til at bruge appen oftere? Vi vil gerne høre det.',
  first_dashboard_visit: 'Du er en af de første der prøver ForbrugerAgenten 🎉 Stødte du på bugs, noget der ikke virkede — eller har du idéer til hvad vi skal bygge?',
}

const FAB_INTRO_TEXT = 'Fortæl os hvad du tænker'
const FAB_COLLAPSE_DELAY_MS = 2000

type CardState = 'hidden' | 'visible' | 'submitted' | 'dismissed'

const FeedbackButton = forwardRef<FeedbackButtonHandle, { screen?: string }>(
  function FeedbackButton({ screen = 'unknown' }, ref) {
    const pathname = usePathname()
    const { isEnabled } = useFeatureFlags()
    const { isDesktop } = useResponsive()
    const {
      submitFeedback, markTriggerFired, hasFiredTrigger,
      getAndIncrementViewCount, getSessionCount, MIN_SESSIONS_FOR_TRIGGERS,
    } = useFeedback()

    const [cardState, setCardState] = useState<CardState>('hidden')
    const [selectedEmoji, setSelectedEmoji] = useState<number | null>(null)
    const [comment, setComment] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [currentTrigger, setCurrentTrigger] = useState<FeedbackTrigger>('manual')
    const [question, setQuestion] = useState('')

    // FAB state
    const [fabExpanded, setFabExpanded] = useState(false)
    const [showIntro, setShowIntro] = useState(false)
    const fabWidth = useSharedValue(48)
    const fabTextOpacity = useSharedValue(0)

    const fabAnimatedStyle = useAnimatedStyle(() => ({
      width: fabWidth.value,
      overflow: 'hidden' as const,
    }))

    const fabTextStyle = useAnimatedStyle(() => ({
      opacity: fabTextOpacity.value,
    }))

    useEffect(() => {
      const count = getAndIncrementViewCount()
      const isIntroView = count < 1

      if (isIntroView) {
        // Start expanded with intro text
        setFabExpanded(true)
        setShowIntro(true)
        fabWidth.value = 260
        fabTextOpacity.value = 1

        // After delay, collapse to icon
        const collapseTimer = setTimeout(() => {
          fabWidth.value = withTiming(48, { duration: 400 })
          fabTextOpacity.value = withTiming(0, { duration: 250 })
          setTimeout(() => {
            setFabExpanded(false)
            setShowIntro(false)
          }, 450)
        }, FAB_COLLAPSE_DELAY_MS)

        return () => clearTimeout(collapseTimer)
      } else {
        fabWidth.value = 48
      }
    }, [])


    // ─── Open card ───
    const openCard = useCallback((trigger: FeedbackTrigger, q?: string) => {
      if (cardState !== 'hidden') return
      setCurrentTrigger(trigger)
      setQuestion(q || TRIGGER_QUESTIONS[trigger])
      setSelectedEmoji(null)
      setComment('')
      setCardState('visible')
      haptics.medium()
      markTriggerFired(trigger)
    }, [cardState, markTriggerFired])

    // No auto-dismiss — user closes with X or submits

    // ─── Cleanup after exit animations ───
    useEffect(() => {
      if (cardState === 'dismissed') {
        const t = setTimeout(() => setCardState('hidden'), 300)
        return () => clearTimeout(t)
      }
      if (cardState === 'submitted') {
        const t = setTimeout(() => setCardState('hidden'), 1500)
        return () => clearTimeout(t)
      }
    }, [cardState])

    // ─── Global trigger + event listener ───
    useImperativeHandle(ref, () => ({ openWithTrigger: openCard }))

    useEffect(() => {
      globalOpenFeedback = openCard
      return () => { globalOpenFeedback = null }
    }, [openCard])

    useEffect(() => {
      const unsub = appEvents.on('open-feedback', (data?: { trigger?: string; question?: string }) => {
        const trigger = (data?.trigger || 'manual') as FeedbackTrigger
        const q = data?.question || TRIGGER_QUESTIONS[trigger]
        if (trigger !== 'manual') {
          if (hasFiredTrigger(trigger)) return
          // first_dashboard_visit + post_onboarding bypass session gate — they target brand-new users
          const bypassesSessionGate = trigger === 'first_dashboard_visit' || trigger === 'post_onboarding'
          if (!bypassesSessionGate && getSessionCount() < MIN_SESSIONS_FOR_TRIGGERS) return
        }
        openCard(trigger, q)
      })
      return unsub
    }, [openCard, hasFiredTrigger, getSessionCount])

    // ─── Handlers ───
    const handleEmojiTap = useCallback((index: number) => {
      haptics.selection()
      setSelectedEmoji(index)
    }, [])

    const handleTextFocus = useCallback(() => {}, [])

    const handleTextChange = useCallback((text: string) => {
      setComment(text)
    }, [])

    const canSubmit = selectedEmoji !== null || comment.trim().length > 0

    const handleSubmit = useCallback(async () => {
      if (!canSubmit || isSubmitting) return
      setIsSubmitting(true)
      haptics.medium()
      const rating = selectedEmoji !== null ? EMOJI_OPTIONS[selectedEmoji].rating : null
      const result = await submitFeedback(rating as number, comment.trim() || null, screen, currentTrigger)
      setIsSubmitting(false)
      if (result.success) {
        haptics.success()
        setCardState('submitted')
      } else {
        haptics.error()
        toast.error('Kunne ikke sende')
      }
    }, [canSubmit, isSubmitting, selectedEmoji, comment, screen, currentTrigger, submitFeedback])

    const handleDismiss = useCallback(() => {
      setCardState('dismissed')
      if (currentTrigger !== 'manual') events.feedbackDismissed(currentTrigger)
    }, [currentTrigger])

    const handleFabPress = useCallback(() => {
      openCard('manual', TRIGGER_QUESTIONS.manual)
    }, [openCard])

    // ─── Visibility gates ───
    if (!isEnabled('feedback_fab', true)) return null
    if (pathname?.includes('/onboarding')) return null
    if (pathname?.includes('/chat')) return null
    if (pathname?.includes('/settings')) return null

    const isCardVisible = cardState !== 'hidden'
    const showFAB = !isCardVisible
    const cardBottom = Platform.OS === 'web' ? 76 : 96
    const fabBottom = Platform.OS === 'web' ? 80 : 100

    return (
      <>
        {/* ─── FAB (always rendered, hidden behind card when card is open) ─── */}
        <View style={[styles.fab, { bottom: fabBottom }, !showFAB && { opacity: 0, pointerEvents: 'none' as any }]}>
          <Pressable onPress={handleFabPress}>
            <Animated.View style={[
              showIntro ? styles.fabExpandedInner : styles.fabInner,
              showIntro && fabAnimatedStyle,
            ]}>
              <MessageSquareHeart size={20} color={colors.text.inverse} />
              {showIntro && (
                <Animated.Text style={[styles.fabLabel, fabTextStyle]}>
                  {FAB_INTRO_TEXT}
                </Animated.Text>
              )}
            </Animated.View>
          </Pressable>
        </View>

        {/* ─── Inline Feedback Card ─── */}
        {cardState === 'visible' && (
          <Animated.View
            entering={SlideInDown.duration(350).springify().damping(18).stiffness(140)}
            exiting={FadeOutDown.duration(250)}
            style={[
              styles.card,
              { bottom: cardBottom },
              isDesktop && { right: 16, left: 'auto' as any, width: 380 },
            ]}
          >
            {/* Header */}
            <View style={styles.cardHeader}>
              <Text style={styles.cardQuestion}>{question}</Text>
              <Pressable onPress={handleDismiss} style={styles.closeBtn}>
                <X size={16} color={colors.text.muted} />
              </Pressable>
            </View>

            {/* SMS info banner — only shown after onboarding */}
            {currentTrigger === 'post_onboarding' && (
              <View style={styles.smsBanner}>
                <MessageCircle size={14} color={colors.primary} />
                <Text style={styles.smsBannerText}>Du modtager SMS med notifikationer om dine aftaler, tilbud og svar fra selskaber.</Text>
              </View>
            )}

            {/* Emoji row */}
            <View style={styles.emojiRow}>
              {EMOJI_OPTIONS.map((opt, i) => (
                <Pressable
                  key={i}
                  onPress={() => handleEmojiTap(i)}
                  style={[styles.emojiBtn, selectedEmoji === i && styles.emojiBtnSelected]}
                >
                  <Text style={styles.emojiText}>{opt.emoji}</Text>
                </Pressable>
              ))}
            </View>

            {/* Textarea + send (always visible) */}
            <TextInput
              value={comment}
              onChangeText={handleTextChange}
              onFocus={handleTextFocus}
              placeholder="Skriv din feedback her..."
              placeholderTextColor={colors.text.muted}
              style={styles.textInput}
              multiline
              maxLength={500}
              textAlignVertical="top"
            />
            <Pressable
              onPress={handleSubmit}
              disabled={!canSubmit || isSubmitting}
              style={[styles.sendBtn, (!canSubmit || isSubmitting) && styles.sendBtnDisabled]}
            >
              <Send size={14} color={colors.text.inverse} />
              <Text style={styles.sendText}>
                {isSubmitting ? 'Sender...' : 'Send'}
              </Text>
            </Pressable>
          </Animated.View>
        )}

        {/* ─── Thank You Card ─── */}
        {cardState === 'submitted' && (
          <Animated.View
            entering={FadeIn.duration(200)}
            exiting={FadeOutDown.duration(300)}
            style={[
              styles.card, styles.thankYouCard, { bottom: cardBottom },
              isDesktop && { right: 16, left: 'auto' as any, width: 380 },
            ]}
          >
            <Text style={styles.thankYouEmoji}>🙏</Text>
            <Text style={styles.thankYouText}>Tak for din feedback!</Text>
          </Animated.View>
        )}
      </>
    )
  }
)

export default FeedbackButton

const styles = StyleSheet.create({
  // ─── FAB ───
  fab: {
    position: 'absolute',
    right: 16,
    zIndex: 200,
  },
  fabInner: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    ...shadows.colored(colors.primary),
  },
  fabExpandedInner: {
    flexDirection: 'row' as const,
    alignItems: 'center',
    height: 48,
    gap: 8,
    paddingHorizontal: 16,
    borderRadius: 24,
    backgroundColor: colors.primary,
    ...shadows.colored(colors.primary),
  },
  fabLabel: {
    ...typography.label,
    color: colors.text.inverse,
    whiteSpace: 'nowrap' as any,
  },

  // ─── Inline Card ───
  card: {
    position: Platform.OS === 'web' ? 'fixed' as any : 'absolute',
    left: 16,
    right: 16,
    maxWidth: 380,
    alignSelf: 'center',
    zIndex: 300,
    backgroundColor: colors.background,
    borderRadius: borderRadius.lg,
    borderWidth: 1,
    borderColor: colors.border,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm + spacing.xs,
    ...shadows.lg,
  },
  smsBanner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.xs,
    backgroundColor: colors.primarySubtle,
    borderRadius: borderRadius.sm,
    padding: spacing.sm,
    marginBottom: spacing.sm,
  },
  smsBannerText: {
    ...typography.small,
    color: colors.primary,
    flex: 1,
    lineHeight: 18,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: spacing.sm,
  },
  cardQuestion: {
    ...typography.bodyBold,
    color: colors.text.primary,
    flex: 1,
    marginRight: spacing.sm,
    lineHeight: 22,
  },
  closeBtn: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surface,
    marginTop: 2,
  },
  emojiRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    paddingHorizontal: spacing.sm,
    marginBottom: spacing.xs,
  },
  emojiBtn: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emojiBtnSelected: {
    backgroundColor: colors.primaryLight,
  },
  emojiText: {
    fontSize: 28,
  },
  textInput: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: borderRadius.sm,
    padding: spacing.sm,
    ...typography.body,
    color: colors.text.primary,
    height: 60,
    marginTop: spacing.xs,
    marginBottom: spacing.sm,
  },
  sendBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    paddingVertical: 10,
    backgroundColor: colors.primary,
    borderRadius: borderRadius.sm,
  },
  sendBtnDisabled: {
    opacity: 0.5,
  },
  sendText: {
    ...typography.label,
    color: colors.text.inverse,
  },

  // ─── Thank You ───
  thankYouCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.md,
  },
  thankYouEmoji: {
    fontSize: 24,
  },
  thankYouText: {
    ...typography.bodyBold,
    color: colors.text.primary,
  },
})
